/**
 * Creator access — who is allowed to promote this brand's products.
 *
 * Two modes, presented as a choice rather than a switch because the wording is
 * what makes the consequence obvious. "Any enrolled creator" is the default and
 * what every brand starts on; "Approved creators only" hides the catalog from
 * everyone the brand has not approved.
 *
 * TWO THINGS THIS SCREEN MUST BE HONEST ABOUT, and both are stated in the UI
 * rather than buried here. BOTH CHANGED once the push and the Requests panel
 * shipped, and the copy went stale in place — a brand read that approvals
 * happened somewhere else, after they had moved here. Worth remembering next
 * time: UI copy that describes an architecture has to be revisited when the
 * architecture moves.
 *
 *  1. **How fast the change lands.** GNBrand stores the decision; GNCreators
 *     enforces it, because creator-facing discovery lives there and it is the
 *     only place that can hide products from a creator's catalog. It is now
 *     PUSHED on save through the webhook outbox rather than riding the catalog
 *     sync, so the lag is the delivery queue, not the sync interval.
 *
 *  2. **Approvals happen here.** They used to be possible only in GNCreators'
 *     Django admin, because this service has no creator records — a creator
 *     exists here only as an opaque reference on a conversion. The Requests
 *     panel now proxies that queue, so a brand approves its own creators
 *     without leaving this screen.
 *
 * Restricting is the destructive direction: a brand that switches to approval
 * mode instantly loses every creator who was promoting it and had not been
 * approved. So that direction asks for confirmation and the other does not.
 */
import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Radio,
  Stack,
  Typography,
} from '@mui/material';
import PublicOutlinedIcon from '@mui/icons-material/PublicOutlined';
import LockPersonOutlinedIcon from '@mui/icons-material/LockPersonOutlined';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import {
  palette,
  radius,
  shadow,
  motion,
} from '../../_helpers/reusableComponents/design/designTokens';
import CommerceService, { readApiError } from '../../_services/commerce/CommerceService';
import CreatorAccessRequests from './CreatorAccessRequests';

const OPEN = 'OPEN';
const APPROVAL = 'APPROVAL';

// Copy lives next to the value it describes so a mode can never be rendered
// with another mode's explanation. Keyed by the API's own values.
const MODE_COPY = {
  [OPEN]: {
    title: 'Any enrolled creator',
    icon: <PublicOutlinedIcon />,
    accent: palette.success,
    summary:
      'Every creator in the affiliate programme can find your products and add them to their storefront.',
    detail:
      'This is how most creator platforms work, and it is the default. More creators promoting you means more reach — restricting access shrinks your own distribution, so only turn it off if you genuinely need to vet who represents the brand.',
  },
  [APPROVAL]: {
    title: 'Approved creators only',
    icon: <LockPersonOutlinedIcon />,
    accent: palette.warning,
    summary:
      'Your products are hidden from every creator you have not approved. They can request access.',
    detail:
      'Creators who are not approved cannot see or search your catalog at all — not just add it. Anyone already promoting you keeps the links they have built, but nobody new can start until you approve them.',
  },
};

const CARD_SX = {
  bgcolor: palette.surface,
  border: `1px solid ${palette.line}`,
  borderRadius: `${radius.md}px`,
  boxShadow: shadow.card,
  p: 3,
};

/** One selectable mode. The whole card is the click target, not just the radio. */
const ModeOption = ({ value, selected, disabled, onSelect }) => {
  const copy = MODE_COPY[value];
  const isSelected = selected === value;

  return (
    <Box
      role="radio"
      aria-checked={isSelected}
      tabIndex={disabled ? -1 : 0}
      onClick={() => !disabled && onSelect(value)}
      onKeyDown={(e) => {
        if (!disabled && (e.key === 'Enter' || e.key === ' ')) {
          e.preventDefault();
          onSelect(value);
        }
      }}
      sx={{
        display: 'flex',
        gap: 2,
        p: 2.5,
        cursor: disabled ? 'default' : 'pointer',
        borderRadius: `${radius.sm}px`,
        border: `1.5px solid ${isSelected ? copy.accent : palette.line}`,
        bgcolor: isSelected ? palette.surfaceAlt : palette.surface,
        transition: `border-color ${motion.base}, background-color ${motion.base}`,
        opacity: disabled ? 0.6 : 1,
        '&:hover': disabled ? {} : { borderColor: copy.accent },
      }}
    >
      <Radio checked={isSelected} disabled={disabled} sx={{ p: 0, mt: 0.25 }} />
      <Box sx={{ flex: 1 }}>
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
          <Box sx={{ color: copy.accent, display: 'flex' }}>{copy.icon}</Box>
          <Typography sx={{ fontWeight: 700, color: palette.ink }}>
            {copy.title}
          </Typography>
          {isSelected && (
            <Chip
              size="small"
              label="Current"
              sx={{
                height: 20,
                fontSize: 11,
                fontWeight: 700,
                color: copy.accent,
                bgcolor: `${copy.accent}1A`,
              }}
            />
          )}
        </Stack>
        <Typography sx={{ fontSize: 14, color: palette.inkSoft, mb: 0.75 }}>
          {copy.summary}
        </Typography>
        <Typography sx={{ fontSize: 13, color: palette.muted, lineHeight: 1.6 }}>
          {copy.detail}
        </Typography>
      </Box>
    </Box>
  );
};

const CreatorAccess = ({ onNotify }) => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [data, setData] = useState(null);
  const [confirming, setConfirming] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const resp = await CommerceService.getCreatorAccess();
      setData(resp.data);
      setError('');
    } catch (err) {
      setError(readApiError(err, 'Could not load creator access settings.'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const save = useCallback(
    async (value) => {
      setSaving(true);
      try {
        const resp = await CommerceService.updateCreatorAccess(value);
        setData(resp.data);
        onNotify?.(
          value === APPROVAL
            ? 'Your catalog is now visible to approved creators only.'
            : 'Your catalog is now open to every enrolled creator.',
          'success',
        );
      } catch (err) {
        onNotify?.(readApiError(err, 'Could not update creator access.'), 'error');
      } finally {
        setSaving(false);
        setConfirming(false);
      }
    },
    [onNotify],
  );

  const handleSelect = (value) => {
    if (!data || value === data.creator_access || saving) return;
    // Restricting is the direction that takes something away — every creator
    // promoting you who is not approved loses the catalog the moment this
    // saves. Opening up takes nothing away, so it needs no confirmation.
    if (value === APPROVAL) {
      setConfirming(true);
      return;
    }
    save(value);
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress size={28} sx={{ color: palette.brand }} />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="warning" sx={{ borderRadius: `${radius.sm}px` }}>
        {error}
      </Alert>
    );
  }

  const current = data?.creator_access || OPEN;

  return (
    <Box sx={CARD_SX}>
      <Typography sx={{ fontWeight: 700, fontSize: 16, color: palette.ink, mb: 0.5 }}>
        Who can promote your products
      </Typography>
      <Typography sx={{ fontSize: 14, color: palette.muted, mb: 2.5 }}>
        This applies to your whole catalog. Per-product access is not available.
      </Typography>

      <Stack spacing={1.5} role="radiogroup" aria-label="Creator access mode">
        <ModeOption
          value={OPEN}
          selected={current}
          disabled={saving}
          onSelect={handleSelect}
        />
        <ModeOption
          value={APPROVAL}
          selected={current}
          disabled={saving}
          onSelect={handleSelect}
        />
      </Stack>

      {/* Both sentences here were wrong until recently and are worth keeping
          honest, because a brand acts on them:

          - the switch used to travel only on the catalog sync, so the note
            said "not immediately". It is now pushed to GNCreators the moment
            it is saved, and only the delivery queue stands between the two;
          - approving individual creators used to be possible only in
            GNCreators' Django admin. The Requests panel below now does it, so
            telling a brand to go elsewhere would send them nowhere. */}
      <Stack
        direction="row"
        spacing={1.25}
        sx={{
          mt: 2.5,
          p: 2,
          borderRadius: `${radius.sm}px`,
          bgcolor: palette.infoSoft,
        }}
      >
        <InfoOutlinedIcon sx={{ fontSize: 20, color: palette.info, mt: '1px' }} />
        <Box>
          <Typography sx={{ fontSize: 13, color: palette.inkSoft, lineHeight: 1.6 }}>
            Changes are sent to creators as soon as you save, and normally take
            effect within a minute.
          </Typography>
          {current === APPROVAL && (
            <Typography
              sx={{ fontSize: 13, color: palette.inkSoft, lineHeight: 1.6, mt: 0.5 }}
            >
              Approve individual creators in the Requests list below.
            </Typography>
          )}
          {typeof data?.creators_with_sales === 'number' && (
            <Typography sx={{ fontSize: 13, color: palette.muted, mt: 0.5 }}>
              {data.creators_with_sales} creator
              {data.creators_with_sales === 1 ? ' has' : 's have'} driven a sale
              for you so far.
            </Typography>
          )}
        </Box>
      </Stack>

      {/* The other half of the gate: who has asked, and the decision. Kept on
          this tab rather than its own, because the setting and the queue it
          produces are one job -- a brand that turns gating on needs to see the
          consequence without hunting for it. */}
      <CreatorAccessRequests onNotify={onNotify} />

      <Dialog open={confirming} onClose={() => !saving && setConfirming(false)}>
        <DialogTitle sx={{ fontWeight: 700 }}>Restrict your catalog?</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ color: palette.inkSoft }}>
            Every creator you have not approved will immediately lose access to
            your products — they will not be able to find them at all. Creators
            already promoting you keep the links they have built, but no new
            creator can start until you approve them below.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={() => setConfirming(false)}
            disabled={saving}
            sx={{ textTransform: 'none', color: palette.muted }}
          >
            Cancel
          </Button>
          <Button
            onClick={() => save(APPROVAL)}
            disabled={saving}
            variant="contained"
            sx={{
              textTransform: 'none',
              fontWeight: 700,
              bgcolor: palette.warning,
              '&:hover': { bgcolor: palette.warning },
            }}
          >
            {saving ? 'Saving…' : 'Restrict access'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CreatorAccess;
